// src/features/treinos/utils/workoutMetrics.js
//
// Resolvedor ÚNICO das métricas do card de treino compartilhável.
//   resolveWorkoutMetrics(workout, profileData) → Metric[]
// já ordenado por prioridade e cortado em MAX_METRICS. Todo ponto de
// renderização (amostra e gerada, todas as variantes) consome esta lista —
// nenhum template decide sozinho o que mostrar.
//
// Regras (decisão 04/09/2026):
//  • máx. 4 métricas; zero repetição; se o dado não existe o slot SOME
//    (nada de "—", "N/A", "0" ou placeholder).
//  • "grupos musculares" fora do layout padrão; o nome dado pelo usuário ao
//    treino vai no TÍTULO do card, não ocupa slot.
//  • o modo vem de CAMPO ESTRUTURADO: `activity_key` → categoria do
//    POINTS_CONFIG. Nunca inferir a partir de `activity_label`/`title`
//    (strings de exibição) — regra anti-heurística.
//  • corrida/caminhada/pedal: duração + distância + ritmo (duração ÷ distância)
//    + kcal. Só duração → duração + kcal. Passos entram só quando NÃO há
//    distância (senão duplicam a mesma informação).
//  • musculação: duração + kcal; séries / volume / exercícios preenchem os
//    slots restantes SOMENTE quando gravados de verdade na atividade
//    (`series_total`, `volume_kg`, `exercises_count` — o check-in ao vivo
//    passou a gravá-los; o manual não tem esses dados e fica com 2).
//  • demais atividades: duração + kcal (+ distância / passos quando existem).
//  • kcal: a gravada na atividade; sem ela, estimativa por MET da categoria
//    (único fallback aceito — sem isso "só duração" não teria kcal). O til
//    de estimativa NÃO é exibido (ver utils/formatKcal.js).
//
// Levantamento no Firestore (set/2026, 1500 registros): as atividades gravam
// activity_key, activity_label, category, duration_min (duration_sec no ao
// vivo), distance_km, calories, steps, points, active_muscles. Nenhum registro
// tinha série/volume — daí a escrita nova no LiveWorkoutContext.

import { POINTS_CONFIG } from './pointsConfig.js';
import { formatKcal } from '../../../utils/formatKcal';

export const MAX_METRICS = 4;

const num = (value) => {
  const normalized = typeof value === 'string' ? value.trim().replace(',', '.') : value;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

// ── Categoria → modo (estruturado, via POINTS_CONFIG) ───────────────────────
const MODE_BY_CATEGORY = {
  'Musculação': 'strength',
  'Corrida': 'endurance',
  'Trilha': 'endurance', // caminhada, trilha, mochilagem
  'Ciclismo': 'endurance',
  'Máquina de cardio': 'endurance', // esteira, elíptica, remo
  'Natação': 'swimming',
};

// MET médio por categoria (Compendium of Physical Activities, Ainsworth et
// al.) — usado SÓ para estimar kcal quando a atividade não gravou calorias.
const MET_BY_CATEGORY = {
  'Musculação': 5.5,
  'Corrida': 9.8,
  'Ciclismo': 8,
  'Máquina de cardio': 7,
  'Mente e corpo': 3,
  'Treinamento funcional': 8,
  'Treino core': 3.8,
  'Aeróbica': 6.5,
  'Artes marciais': 8.5,
  'Esportes em equipe': 7.5,
  'Trilha': 4.8,
  'Escalada': 8,
  'Esporte de raquete': 7,
  'Esportes recreativos': 5,
  'Natação': 8,
  'Mais': 6,
};
const MET_DEFAULT = 6;
const DEFAULT_WEIGHT_KG = 70;

// Categoria estruturada da atividade: pelo activity_key no catálogo; o campo
// `category` gravado no doc é o segundo caminho (mesma origem, snapshot).
export const activityCategory = (activity = {}) => {
  const key = activity?.activity_key ?? activity?.key;
  return POINTS_CONFIG[key]?.category || activity?.category || null;
};

export const activityMode = (activity = {}) =>
  MODE_BY_CATEGORY[activityCategory(activity)] || 'default';

// duration_min (manual) OU duration_sec (treino ao vivo).
export const activityDurationMin = (activity = {}) => {
  const min = num(activity?.duration_min);
  return min > 0 ? min : num(activity?.duration_sec) / 60;
};

const profileWeight = (profileData = {}) => num(
  profileData?.CURRENT_WEIGHT
  ?? profileData?.PESO_ATUAL
  ?? profileData?.PESO
  ?? profileData?.weight
  ?? profileData?.peso
  ?? profileData?.DADOS_BIOLOGICOS?.PESO,
) || DEFAULT_WEIGHT_KG;

// kcal ≈ MET × 3,5 × peso(kg) × minutos ÷ 200
const estimateActivityCalories = (activity, weightKg) => {
  const duration = activityDurationMin(activity);
  if (duration <= 0) return 0;
  const met = MET_BY_CATEGORY[activityCategory(activity)] || MET_DEFAULT;
  return (met * 3.5 * weightKg * duration) / 200;
};

// ── Totais ───────────────────────────────────────────────────────────────────
/**
 * Soma os campos estruturados de `workout.atividades[]`. Não lê nada do
 * `title`/`description`.
 * @returns {{ mode: string, totals: object, caloriesEstimated: boolean }}
 */
export const summarizeWorkout = (workout = {}, profileData = {}) => {
  const activities = Array.isArray(workout?.atividades) ? workout.atividades : [];
  const weightKg = profileWeight(profileData);

  const totals = {
    duration: 0, distance: 0, steps: 0, calories: 0,
    series: 0, volume: 0, exercises: 0,
  };
  let caloriesEstimated = false;

  activities.forEach((activity) => {
    totals.duration += activityDurationMin(activity);
    totals.distance += num(activity?.distance_km);
    totals.steps += num(activity?.steps);
    totals.series += num(activity?.series_total);
    totals.volume += num(activity?.volume_kg);
    totals.exercises += num(activity?.exercises_count);

    const recorded = num(activity?.calories);
    if (recorded > 0) {
      totals.calories += recorded;
    } else {
      const estimated = estimateActivityCalories(activity, weightKg);
      if (estimated > 0) caloriesEstimated = true;
      totals.calories += estimated;
    }
  });

  // Modo = atividade dominante (maior duração).
  const dominant = activities.reduce((best, activity) => (
    activityDurationMin(activity) > activityDurationMin(best) ? activity : best
  ), activities[0] || {});

  return { mode: activityMode(dominant), totals, caloriesEstimated };
};

// ── Formatação ───────────────────────────────────────────────────────────────
const ptBR = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits }).format(value);

const metric = (key, value, unit, label) => ({
  key, value, unit, label, text: unit ? `${value} ${unit}` : value,
});

const durationMetric = (minutes) => {
  const total = Math.round(minutes);
  if (total <= 0) return null;
  if (total < 60) return metric('duration', String(total), 'min', 'duração');
  const h = Math.floor(total / 60);
  const m = String(total % 60).padStart(2, '0');
  return metric('duration', `${h}h${m}`, '', 'duração');
};

const distanceMetric = (km) => (km > 0 ? metric('distance', ptBR(km, 1), 'km', 'distância') : null);

// Ritmo derivado (duração ÷ distância). `perKm` = 1 → min/km; 10 → min/100m.
const paceMetric = (minutes, km, { perKm = 1, unit = '/km' } = {}) => {
  if (minutes <= 0 || km <= 0) return null;
  const totalSeconds = Math.round((minutes * 60) / (km * perKm));
  if (totalSeconds <= 0) return null;
  const m = Math.floor(totalSeconds / 60);
  const s = String(totalSeconds % 60).padStart(2, '0');
  return metric('pace', `${m}:${s}`, unit, 'ritmo');
};

const caloriesMetric = (kcal) => (kcal > 0 ? metric('calories', formatKcal(kcal), 'kcal', 'calorias') : null);
const stepsMetric = (steps) => (steps > 0 ? metric('steps', ptBR(steps), 'passos', 'passos') : null);
const seriesMetric = (series) => (series > 0 ? metric('series', ptBR(series), 'séries', 'séries') : null);
const volumeMetric = (kg) => (kg > 0 ? metric('volume', ptBR(kg), 'kg', 'volume') : null);
const exercisesMetric = (n) => (n > 0 ? metric('exercises', ptBR(n), 'exercícios', 'exercícios') : null);

// ── Resolvedor ───────────────────────────────────────────────────────────────
/**
 * @param {object} workout      registro de treino (shape do REGISTRO_TREINOS)
 * @param {object} profileData  perfil (peso p/ estimativa de kcal)
 * @returns {Array<{key:string,value:string,unit:string,label:string,text:string}>}
 */
export const resolveWorkoutMetrics = (workout = {}, profileData = {}) => {
  const { mode, totals } = summarizeWorkout(workout, profileData);
  const duration = durationMetric(totals.duration);
  const calories = caloriesMetric(totals.calories);
  const distance = distanceMetric(totals.distance);
  // Passos só sem distância — com distância seriam a mesma informação.
  const steps = distance ? null : stepsMetric(totals.steps);

  let ordered;
  if (mode === 'endurance') {
    ordered = [duration, distance, paceMetric(totals.duration, totals.distance), calories, steps];
  } else if (mode === 'swimming') {
    ordered = [
      duration, distance,
      paceMetric(totals.duration, totals.distance, { perKm: 10, unit: '/100m' }),
      calories,
    ];
  } else if (mode === 'strength') {
    ordered = [
      duration, calories,
      seriesMetric(totals.series), volumeMetric(totals.volume), exercisesMetric(totals.exercises),
    ];
  } else {
    ordered = [duration, calories, distance, steps];
  }

  const seen = new Set();
  return ordered
    .filter((item) => item && !seen.has(item.key) && seen.add(item.key))
    .slice(0, MAX_METRICS);
};

export default resolveWorkoutMetrics;
